import React, { useState } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

const SignUp: React.FC = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const prefilledEmail =
    searchParams.get('email') ||
    (location.state as { email?: string } | null)?.email ||
    ''

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState(prefilledEmail)
  const [code, setCode] = useState('')
  const [sentEmail, setSentEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { sendMagicCode, verifyMagicCode } = useAuth()
  const navigate = useNavigate()

  const inputClass =
    'w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/40 transition-all duration-300'

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { error: sendError } = await sendMagicCode(email)
      if (sendError) {
        setError(sendError.message)
      } else {
        try {
          sessionStorage.setItem('pending_signup_name', fullName.trim())
          sessionStorage.setItem('pending_signup_email', email.trim().toLowerCase())
        } catch {
          /* ignore */
        }
        setSentEmail(email.trim().toLowerCase())
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { error: verifyError } = await verifyMagicCode(sentEmail, code, fullName)
      if (verifyError) {
        setError(verifyError.message)
      } else {
        try {
          sessionStorage.removeItem('pending_signup_name')
          sessionStorage.removeItem('pending_signup_email')
        } catch {
          /* ignore */
        }
        navigate('/dashboard', { replace: true })
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-8rem)] flex items-center justify-center overflow-hidden bg-ink texture-dots px-4 py-16 sm:px-6 sm:py-20">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-brand/15 rounded-full blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute -bottom-40 -right-32 w-[380px] h-[380px] bg-gold/5 rounded-full blur-3xl pointer-events-none" aria-hidden />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-10">
          <img
            src="/images/logo-light.webp"
            alt="Fit by Cassie"
            className="h-14 sm:h-16 w-auto mx-auto mb-8"
          />
          <div className="divider-gold mx-auto mb-8" />
          <h1 className="font-display text-3xl sm:text-4xl text-white leading-snug mb-4">
            Your strongest self <em className="italic text-gold-light">starts here</em>.
          </h1>
          <p className="text-gray-400 leading-relaxed max-w-sm mx-auto">
            {sentEmail
              ? `We emailed a code to ${sentEmail}. Enter it to finish creating your account.`
              : prefilledEmail
                ? "We didn't find an account for that email — create one below to get started."
                : 'Create your account with a one-time email code — no password to remember.'}
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-400/30 text-red-300 px-4 py-3 rounded-xl mb-6 text-sm"
          >
            {error}
          </motion.div>
        )}

        {!sentEmail ? (
          <form onSubmit={handleSendCode} className="space-y-5">
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-gray-300 mb-2">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={inputClass}
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@example.com"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full btn-gold !py-4">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ink" />
              ) : (
                <>
                  Send Code
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <label htmlFor="code" className="block text-sm font-semibold text-gray-300 mb-2">
                Verification code
              </label>
              <input
                id="code"
                type="text"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={inputClass}
                placeholder="123456"
                autoFocus
              />
            </div>
            <button type="submit" disabled={loading} className="w-full btn-gold !py-4">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ink" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setSentEmail('')
                setCode('')
              }}
              className="w-full text-sm text-gray-400 hover:text-gold-light"
            >
              Use a different email
            </button>
          </form>
        )}

        <div className="mt-8 pt-8 border-t border-white/10 text-center">
          <p className="text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-gold-light font-semibold hover:text-gold transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default SignUp
